import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { API_BASE } from '../config'

export interface SiteSettings {
  site_name?: string | null
  tagline?: string | null
  address_line1?: string | null
  address_line2?: string | null
  city?: string | null
  state?: string | null
  postal_code?: string | null
  country?: string | null
  phone?: string | null
  phone_alt?: string | null
  email?: string | null
  whatsapp?: string | null
  map_url?: string | null
}

export interface SocialLink {
  id: number
  platform: string
  url: string
  sort_order: number
}

interface SiteSettingsState {
  loading: boolean
  settings: SiteSettings | null
  socialLinks: SocialLink[]
}

const Ctx = createContext<SiteSettingsState>({
  loading: true,
  settings: null,
  socialLinks: [],
})

export function SiteSettingsProvider({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true)
  const [settings, setSettings] = useState<SiteSettings | null>(null)
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([])

  useEffect(() => {
    let cancelled = false
    async function run() {
      try {
        const res = await fetch(`${API_BASE}/api/public/settings`)
        const data = await res.json()
        if (cancelled) return
        if (data?.success) {
          setSettings(data.settings || null)
          setSocialLinks(Array.isArray(data.socialLinks) ? data.socialLinks : [])
        }
      } catch {
        // fallback to defaults in UI
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [])

  const value = useMemo(() => ({ loading, settings, socialLinks }), [loading, settings, socialLinks])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useSiteSettings() {
  return useContext(Ctx)
}

